import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import CapexaraCognitiveSubstrate from "./CapexaraCognitiveSubstrate";

/**
 * Architecture modal — opened from the homepage Cognitive Spine "See the
 * architecture" button. Renders the existing substrate SVG enlarged with
 * three band annotations explaining the layered design.
 *
 * No external deps. ESC + backdrop click close it. Body scroll locked
 * while open.
 */
export default function ArchitectureModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="arch-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="arch-modal-title"
      onClick={onClose}
      data-testid="arch-modal"
    >
      <div className="arch-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="arch-modal-close"
          onClick={onClose}
          aria-label="Close architecture view"
          data-testid="arch-modal-close"
        >
          ×
        </button>

        <header className="arch-modal-head">
          <div className="arch-modal-eyebrow">
            <span className="dot" />The Capexara cognitive architecture
          </div>
          <h2 id="arch-modal-title" className="arch-modal-h2">
            Three bands. <em>One brain.</em>
          </h2>
          <p className="arch-modal-sub">
            Every Capexara surface — from a procurement query to a schedule
            forensic — runs through the same three layers. Read top-down: this
            is what one cognition engine looks like.
          </p>
        </header>

        <div className="arch-modal-canvas" aria-hidden="true">
          <CapexaraCognitiveSubstrate />
        </div>

        <ol className="arch-modal-bands" data-testid="arch-modal-bands">
          <li className="arch-band" data-testid="arch-band-apps">
            <div className="arch-band-num">01</div>
            <div className="arch-band-body">
              <div className="arch-band-label">Application surface</div>
              <h3 className="arch-band-title">Seven domain-trained platforms</h3>
              <p className="arch-band-copy">
                Luminex, Assayo, Evalore, Docuweave, Formigen, Panorix, ProcureX —
                each a sharp tool for one phase of the capital-project lifecycle.
                Stand-alone, but never solo: every query they raise enters the
                cognition band below.
              </p>
            </div>
          </li>
          <li className="arch-band" data-testid="arch-band-cognition">
            <div className="arch-band-num">02</div>
            <div className="arch-band-body">
              <div className="arch-band-label">Cognition</div>
              <h3 className="arch-band-title">Cognara — the reasoning router</h3>
              <p className="arch-band-copy">
                Routes a single natural-language question across the platforms
                in parallel, retrieves grounded context from each, plans the
                next move, and assembles a cited answer. One question in,
                twelve datapoints synthesised, one confident response out.
              </p>
            </div>
          </li>
          <li className="arch-band" data-testid="arch-band-substrate">
            <div className="arch-band-num">03</div>
            <div className="arch-band-body">
              <div className="arch-band-label">Substrate</div>
              <h3 className="arch-band-title">Domain training, governance, telemetry</h3>
              <p className="arch-band-copy">
                Capital-project ontology, role-based access, audit trails, and
                evaluation harnesses sit beneath every platform. Switch a
                product on or off — the substrate, and the answers it produces,
                stay consistent.
              </p>
            </div>
          </li>
        </ol>

        <footer className="arch-modal-foot">
          <Link
            to="/platforms"
            className="btn btn-primary"
            onClick={onClose}
            data-testid="arch-modal-explore"
          >
            Explore all eight platforms <span className="arrow">→</span>
          </Link>
          <Link
            to="/platforms/cognara"
            className="arch-modal-link"
            onClick={onClose}
            data-testid="arch-modal-sutradhar"
          >
            Meet Cognara <span className="arrow">→</span>
          </Link>
        </footer>
      </div>
    </div>
  );
}
